import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './PartnerFeed.css'

const API_BASE = 'https://backend.alephmedia.ro/api'

const FILTER_TYPES = [
  { value: 'topic', label: 'Subiect' },
  { value: 'person', label: 'Persoană' },
  { value: 'location', label: 'Locație' },
  { value: 'event', label: 'Eveniment' },
  { value: 'role', label: 'Rol' },
  { value: 'org', label: 'Organizație' },
]

const MATCH_MODES = [
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
]

function PartnerFeed() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [stories, setStories] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [selectedStory, setSelectedStory] = useState(null)

  const [showConfigure, setShowConfigure] = useState(false)
  const [feedStatus, setFeedStatus] = useState(null)
  const [configFilters, setConfigFilters] = useState([])
  const [configSelectionPrompt, setConfigSelectionPrompt] = useState('')
  const [configRewritePrompt, setConfigRewritePrompt] = useState('')
  const [configLanguage, setConfigLanguage] = useState('ro')
  const [configSaving, setConfigSaving] = useState(false)

  const token = localStorage.getItem('authToken')

  useEffect(() => {
    if (searchParams.get('configure') === '1') {
      fetchFeedStatus().then(() => setShowConfigure(true))
    }
  }, [])

  useEffect(() => {
    fetchStories(page)
  }, [page])

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json',
  }

  const fetchStories = async (p) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE}/partner-feed/stories?page=${p}&per_page=20`, { headers })
      if (response.status === 403) {
        navigate('/v4.0/account')
        return
      }
      if (!response.ok) throw new Error('Nu s-au putut încărca știrile')
      const data = await response.json()
      setStories(data.data || [])
      setMeta(data.meta || null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchFeedStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/partner-feed/status`, { headers })
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          setFeedStatus(data.data)
          setConfigFilters(data.data.filters || [])
          setConfigSelectionPrompt(data.data.selection_prompt || '')
          setConfigRewritePrompt(data.data.rewrite_prompt || '')
          setConfigLanguage(data.data.language || 'ro')
        }
      }
    } catch (err) {
      console.error('Failed to fetch feed status', err)
    }
  }

  const handleStoryClick = async (story) => {
    setSelectedStory(story)
    if (!story.read_at) {
      try {
        await fetch(`${API_BASE}/partner-feed/stories/${story.id}/read`, {
          method: 'POST',
          headers,
        })
        setStories(prev => prev.map(s =>
          s.id === story.id ? { ...s, read_at: new Date().toISOString() } : s
        ))
      } catch (err) {
        console.error('Failed to mark story as read', err)
      }
    }
  }

  const handleAddFilter = () => {
    setConfigFilters([...configFilters, { filter_type: 'topic', filter_value: '', match_mode: 'include' }])
  }

  const handleRemoveFilter = (index) => {
    setConfigFilters(configFilters.filter((_, i) => i !== index))
  }

  const handleFilterChange = (index, field, value) => {
    setConfigFilters(configFilters.map((f, i) => i === index ? { ...f, [field]: value } : f))
  }

  const handleSaveConfig = async () => {
    setConfigSaving(true)
    setError(null)
    try {
      const validFilters = configFilters.filter(f => f.filter_value.trim())
      const response = await fetch(`${API_BASE}/partner-feed/filters`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filters: validFilters,
          selection_prompt: configSelectionPrompt || null,
          rewrite_prompt: configRewritePrompt || null,
          language: configLanguage,
        }),
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Nu s-au putut salva filtrele')
      }
      setShowConfigure(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setConfigSaving(false)
    }
  }

  const openConfigure = () => {
    fetchFeedStatus().then(() => setShowConfigure(true))
  }

  const stripHtml = (html) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  if (selectedStory) {
    return (
      <div className="v4-feed-root">
        <div className="v4-feed-container">
          <div className="v4-feed-story-detail">
            <div className="v4-feed-story-detail-header">
              <button
                className="v4-feed-story-detail-close"
                onClick={() => setSelectedStory(null)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <div className="v4-feed-story-meta">
                {selectedStory.category && (
                  <span className="v4-feed-story-category">{selectedStory.category}</span>
                )}
                {selectedStory.received_at && (
                  <span className="v4-feed-story-date">
                    {new Date(selectedStory.received_at).toLocaleDateString('ro-RO', {
                      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                )}
              </div>
              <h2 className="v4-feed-story-title" style={{ fontSize: 'clamp(20px, 4vw, 24px)' }}>
                {selectedStory.title}
              </h2>
            </div>
            {selectedStory.image_url && (
              <img src={selectedStory.image_url} alt="" className="v4-feed-story-image" style={{ height: 'auto', maxHeight: '400px' }} />
            )}
            <div
              className="v4-feed-story-detail-content"
              dangerouslySetInnerHTML={{ __html: selectedStory.content }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="v4-feed-root">
      <div className="v4-feed-container">
        <div className="v4-feed-header">
          <button className="v4-feed-back-btn" onClick={() => navigate('/v4.0/account')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <h1>Feed Știri</h1>
          <div className="v4-feed-header-actions">
            <button className="v4-feed-configure-btn" onClick={openConfigure}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Filtre
            </button>
          </div>
        </div>

        {error && <div className="v4-feed-error">{error}</div>}

        {loading ? (
          <div className="v4-feed-loading">
            <div className="v4-feed-spinner"></div>
            <p>Se încarcă știrile...</p>
          </div>
        ) : stories.length === 0 ? (
          <div className="v4-feed-empty">
            <svg className="v4-feed-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 11a9 9 0 0 1 9 9" />
              <path d="M4 4a16 16 0 0 1 16 16" />
              <circle cx="5" cy="19" r="1" />
            </svg>
            <h3>Niciun articol momentan</h3>
            <p>Știrile vor apărea aici pe măsură ce sunt procesate de Alephia.</p>
          </div>
        ) : (
          <>
            <div className="v4-feed-stories">
              {stories.map(story => (
                <div
                  key={story.id}
                  className={`v4-feed-story-card ${!story.read_at ? 'v4-feed-story-card--unread' : ''}`}
                  onClick={() => handleStoryClick(story)}
                >
                  {story.image_url && (
                    <img src={story.image_url} alt="" className="v4-feed-story-image" />
                  )}
                  <div className="v4-feed-story-body">
                    <div className="v4-feed-story-meta">
                      {!story.read_at && <span className="v4-feed-story-unread-dot" />}
                      {story.category && (
                        <span className="v4-feed-story-category">{story.category}</span>
                      )}
                      {story.received_at && (
                        <span className="v4-feed-story-date">
                          {new Date(story.received_at).toLocaleDateString('ro-RO', {
                            year: 'numeric', month: 'short', day: 'numeric'
                          })}
                        </span>
                      )}
                    </div>
                    <h3 className="v4-feed-story-title">{story.title}</h3>
                    <p className="v4-feed-story-excerpt">{stripHtml(story.content)}</p>
                  </div>
                </div>
              ))}
            </div>

            {meta && meta.last_page > 1 && (
              <div className="v4-feed-pagination">
                <button
                  className="v4-feed-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </button>
                <span className="v4-feed-page-info">
                  {meta.current_page} / {meta.last_page}
                </span>
                <button
                  className="v4-feed-page-btn"
                  disabled={page >= meta.last_page}
                  onClick={() => setPage(p => p + 1)}
                >
                  Următor
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showConfigure && (
        <div className="v4-feed-configure-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowConfigure(false)
        }}>
          <div className="v4-feed-configure-modal">
            <div className="v4-feed-configure-header">
              <h2>Configurare Feed</h2>
              <button className="v4-feed-configure-close" onClick={() => setShowConfigure(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="v4-feed-configure-body">
              <div className="v4-feed-configure-section">
                <h3>Filtre</h3>
                <div className="v4-feed-configure-filters">
                  {configFilters.map((filter, i) => (
                    <div key={i} className="v4-feed-configure-filter-row">
                      <select
                        value={filter.filter_type}
                        onChange={(e) => handleFilterChange(i, 'filter_type', e.target.value)}
                      >
                        {FILTER_TYPES.map(ft => (
                          <option key={ft.value} value={ft.value}>{ft.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={filter.filter_value}
                        onChange={(e) => handleFilterChange(i, 'filter_value', e.target.value)}
                        placeholder="Valoare..."
                      />
                      <select
                        value={filter.match_mode}
                        onChange={(e) => handleFilterChange(i, 'match_mode', e.target.value)}
                      >
                        {MATCH_MODES.map(mm => (
                          <option key={mm.value} value={mm.value}>{mm.label}</option>
                        ))}
                      </select>
                      <button
                        className="v4-feed-configure-remove-btn"
                        onClick={() => handleRemoveFilter(i)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button className="v4-feed-configure-add-btn" onClick={handleAddFilter}>
                    + Adaugă filtru
                  </button>
                </div>
              </div>

              <div className="v4-feed-configure-section">
                <h3>Prompt selecție</h3>
                <textarea
                  className="v4-feed-configure-textarea"
                  value={configSelectionPrompt}
                  onChange={(e) => setConfigSelectionPrompt(e.target.value)}
                  placeholder="Descrie ce tip de știri dorești să primești..."
                />
              </div>

              <div className="v4-feed-configure-section">
                <h3>Prompt rescriere</h3>
                <textarea
                  className="v4-feed-configure-textarea"
                  value={configRewritePrompt}
                  onChange={(e) => setConfigRewritePrompt(e.target.value)}
                  placeholder="Descrie cum vrei să fie rescrise știrile..."
                />
              </div>

              <div className="v4-feed-configure-section">
                <h3>Limba</h3>
                <select
                  style={{ padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', width: '100%' }}
                  value={configLanguage}
                  onChange={(e) => setConfigLanguage(e.target.value)}
                >
                  <option value="ro">Română</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>

            <div className="v4-feed-configure-footer">
              <button className="v4-feed-configure-cancel" onClick={() => setShowConfigure(false)}>
                Anulează
              </button>
              <button
                className="v4-feed-configure-save"
                onClick={handleSaveConfig}
                disabled={configSaving}
              >
                {configSaving ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PartnerFeed
